import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { RequirementConfigurationService } from '@app/shared/services/requirement-configuration.service';
import { AppConsts } from '@shared/AppConsts';
import { SelectItem } from 'primeng/api';
import { Observable, of, ReplaySubject } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { ApplicationStatus, ApplicationType, ApplicationUploadFilesInput, CreateLiaTypeInput, CreateLiaTypeOutput, CreateOrUpdateEhcElectronicPatientRecordInput, CreateOrUpdateEhcEmploymentOrPostgraduateTitleInput, CreateOrUpdateEhcLanguageSkillInput, CreateOrUpdateEhcQualityRequirementInput, CreateOrUpdateEhcSelfEmploymentInput, CreateOrUpdateLiaAcademicTitleInput, CreateOrUpdateLiaAcademicTitleOutput, CreateOrUpdateLiaContactInput, CreateOrUpdateLiaEmploymentOrPostgraduateTitlesOutput, CreateOrUpdateLiaEmploymentsInput, CreateOrUpdateLiaEmploymentsInWaitingStatusOutput, CreateOrUpdateLiaEmploymentsOutput, CreateOrUpdateLiaInvoiceAddressInput, CreateOrUpdateLiaLanguageSkillInput, CreateOrUpdateLiaPersonOutputDto, CreateOrUpdateLiaPersonWithoutRegisterCheckInput, CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto, CreateOrUpdateLiaPersonWithRegisterCheckInput, CreateOrUpdateLiaPracticesInput, CreateOrUpdateLiaPracticesOutput, CreateOrUpdateLiaRestrictionsInput, CreateOrUpdateLiaTherapyMethodInput, CreateOrUpdateLiaVaccinationInputDto, EhcElectronicPatientRecordDto, EhcEmploymentOrPostgraduateTitleDto, EhcEmploymentOrPostgraduateTitleLimitResultDto, EhcLanguageSkillDto, EhcQualityRequirementDto, EhcSelfEmploymentDto, ExternalSystemEnum, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetLiaOutput, LiaAcademicTitleDto, LiaAcademicTitleServiceProxy, LiaContactDto, LiaContactServiceProxy, LiaDocumentServiceProxy, LiaDocumentsOutput, LiaDto, LiaEhcLanguageSkillServiceProxy, LiaElectronicPatientRecordServiceProxy, LiaEmploymentDto, LiaEmploymentOrPostgraduateTitleServiceProxy, LiaEmploymentOutput, LiaEmploymentsServiceProxy, LiaInvoiceAddressDto, LiaInvoiceAddressServiceProxy, LiaLanguageSkillDto, LiaLanguageSkillServiceProxy, LiaPersonDto, LiaPersonServiceProxy, LiaPracticeDto, LiaPracticeServiceProxy, LiaProfessionalResponsibilityServiceProxy, LiaQualityRequirementServiceProxy, LiaRestrictionOutput, LiaRestrictionsServiceProxy, LiaSelfEmploymentServiceProxy, LiaStatus, LiaTherapyMethodDto, LiaTherapyMethodServiceProxy, LiaTypeServiceProxy, LiaVaccinationDto, LiaVaccinationServiceProxy, PersonApplicationSummaryDto, ProfessionalLicenceDto, RequirementDto, RequirementType, SetLiaOnWaitingAndCreateEhcOutput } from './../../../shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';

export class LiaInvoiceAddressDetail {
    name!: string | undefined;
    street!: string;
    streetNr!: string | undefined;
    postCode!: string;
    city!: string;
    email!: string;
    countryId!: string | undefined;

    constructor(data?: LiaInvoiceAddressDetail) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)) {
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }
}

@Injectable({providedIn: 'root'})
export class LiaService extends AppComponentBase {

    lia: LiaDto;
    liaResultDocuments: GeneralFileInfoDto[];
    onlyPrivateAddressAllowed = false;

    private liasForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly liasForPerson$ = this.liasForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private requirementConfigurationService: RequirementConfigurationService,
        private liaTypeService: LiaTypeServiceProxy,
        private liaPersonService: LiaPersonServiceProxy,
        private liaContactService: LiaContactServiceProxy,
        private liaEmploymentsService: LiaEmploymentsServiceProxy,
        private liaLanguageSkillService: LiaLanguageSkillServiceProxy,
        private liaInvoiceAddressService: LiaInvoiceAddressServiceProxy,
        private liaDocumentService: LiaDocumentServiceProxy,
        private liaAcademicTitleService: LiaAcademicTitleServiceProxy,
        private liaProfessionalResponsibilityService: LiaProfessionalResponsibilityServiceProxy,
        private liaRestrictionService: LiaRestrictionsServiceProxy,
        private liaVaccinationService: LiaVaccinationServiceProxy,
        private liaTherapyMethodService: LiaTherapyMethodServiceProxy,
        private liaSelfEmploymentService: LiaSelfEmploymentServiceProxy,
        private ehcLanguageSkillService: LiaEhcLanguageSkillServiceProxy,
        private liaQualityRequirementService: LiaQualityRequirementServiceProxy,
        private liaEmploymentOrPostgraduateTitleService: LiaEmploymentOrPostgraduateTitleServiceProxy,
        private liaPracticeService: LiaPracticeServiceProxy,
        private liaElectronicPatientRecordService: LiaElectronicPatientRecordServiceProxy,
    ) {
        super(injector);
        console.log('LiaService constructor');
    }

    loadLiasForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.liaTypeService.getLiasForPerson(gln).pipe(
                tap(lias => this.liasForPersonSubject.next(lias))
            );
        } else {
            this.liasForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaTypeService.getExternalRegisterByProfessionId(professionId);
    }

    getFailedLiaForProfessionAndGln(caseId: string): Observable<GetLiaOutput> {
        return this.liaTypeService.getFailedLiaForProfessionAndGln(caseId);
    }

    createLia(input: CreateLiaTypeInput): Observable<CreateLiaTypeOutput> {
        return this.liaTypeService.createLiaType(input).pipe(tap((output: CreateLiaTypeOutput) => {
            this.lia = null;
        }));
    }

    getLia(caseId: string): Observable<GetLiaOutput> {
        return this.liaTypeService.getLia(caseId).pipe(
            tap((output: GetLiaOutput) => {
                this.lia = output.lia;
                return this.requirementConfigurationService.getRequirementByProfessionAndType(this.lia.professionId, RequirementType.OnlyPrivateAddressAllowed).subscribe((requirement: RequirementDto) => {
                    this.onlyPrivateAddressAllowed = requirement.value === this.codes.yesNo.yes;
                });
            })
        );
    }

    closeLia(): void {
        this.lia = null;
    }

    getMyLias(): Observable<PersonApplicationSummaryDto[]> {
        return this.liaTypeService.getMyLias();
    }

    isInLiaStatus(expectedStatus: LiaStatus): boolean {
        return this.lia.currentStep === expectedStatus;
    }

    get currentStep(): LiaStatus {
        return this.lia.currentStep;
    }

    getProfessionalLicencePersonIdForApplication(): Observable<ProfessionalLicenceDto> {
        return this.liaTypeService.getProfessionalLicenceForLia(this.applicationService.caseId);
    }

    getEvaluationComments(): Observable<string> {
        return this.liaTypeService.getEvaluationComments(this.applicationService.caseId);
    }

    releaseLia(comment: string): Observable<void> {
        return this.liaTypeService.releaseLia(this.lia.application.caseId, comment);
    }

    getNextStatus(status: LiaStatus): Observable<LiaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.liaTypeService.getNextStatus(this.applicationService.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === LiaStatus.Release) {
                return LiaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: LiaStatus): string {
        return this.applicationService.getNextStep(LiaService.getUrlForStep(nextStep));
    }

    getFile(step: LiaStatus, id: number): Observable<GeneralFileDto> {
        return this.liaDocumentService.getFile(this.applicationService.caseId, step, id);
    }

    deleteFile(step: LiaStatus, id: number): Observable<void> {
        return this.liaDocumentService.deleteFile(this.applicationService.caseId, step, id);
    }

    get caseId(): string {
        return this.lia.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.lia.application.status;
    }

    get profession(): string {
        return this.lia?.professionId;
    }

    get medRegId(): number {
        return this.lia.liaPerson.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.lia.liaPerson.medRegId != null;
    }

    get psyRegId(): number {
        return this.lia.liaPerson.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.lia.liaPerson.psyRegId != null;
    }

    get naregId(): number {
        return this.lia.liaPerson.naregId;
    }

    get hasNaregId(): boolean {
        return this.lia.liaPerson.naregId != null;
    }

    get numberOfLinkedEhcApplications(): number {
        return this.lia.numberOfLinkedEhcApplications;
    }

    get hasAnyLinkedEhcUnderConstruction(): boolean {
        const statuses = this.lia?.statusOfLinkedApplications;
        return statuses?.some(x => x === ApplicationStatus.UnderConstruction);
    }

    getPerson(): Observable<LiaPersonDto> {
        return of(this.lia.liaPerson).pipe(delay(1));
    }

    setLiaPersonStepToReview() {
        this.applicationService.setStepToReview(LiaStatus.Person);
    }

    createOrUpdateLiaPersonWithRegisterCheck(input: CreateOrUpdateLiaPersonWithRegisterCheckInput): Observable<CreateOrUpdateLiaPersonOutputDto> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonWithRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonOutputDto) => {
            this.lia.liaPerson = output.liaPerson;
        }));
    }

    createOrUpdateLiaPersonWithoutRegisterCheck(input: CreateOrUpdateLiaPersonWithoutRegisterCheckInput): Observable<CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonWithoutRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto) => {
            this.lia.liaPerson = output.liaPerson;
        }));
    }

    acceptRegisterCheck(): Observable<LiaStatus> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        }));
    }

    getLiaPersonFiles(forceRefresh?: boolean, hasLicenceInAnotherCanton?: boolean, nationality?: string, noRegister?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaPersonService.getLiaPersonDocuments(caseId, LiaStatus.Person, hasLicenceInAnotherCanton, nationality, noRegister).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaPerson.personDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaPerson.personDocuments);
    }

    uploadLiaPersonFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPersonService.uploadFiles(input);
    }


    get hasLicenceInOtherCanton(): boolean {
        return this.lia.liaPerson.hasLicenceInAnotherCantonId === this.codes.yesNo.yes;
    }

    getRestrictions(): Observable<LiaRestrictionOutput> {
        return of(this.lia.liaRestrictions).pipe(delay(1));
    }

    createOrUpdateLiaRestrictions(input: CreateOrUpdateLiaRestrictionsInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Restriction);
        return this.liaRestrictionService.createOrUpdateLiaRestrictions(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaRestrictions.restrictions = input.liaRestrictions;
            this.lia.liaRestrictions.generalInformation = input.generalInformation;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    getContact(): Observable<LiaContactDto> {
        return of(this.lia.liaContact).pipe(delay(0));
    }

    createOrUpdateLiaContact(input: CreateOrUpdateLiaContactInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Contact);
        return this.liaContactService.createOrUpdateLiaContact(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaContact = input.liaContact;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    getAcademicTitles(): Observable<LiaAcademicTitleDto[]> {
        return of(this.lia.liaAcademicTitles.titles);
    }

    createBlankLiaAcademicTitle(): Observable<string> {
        return this.liaAcademicTitleService.createBlankLiaAcademicTitle(this.caseId);
    }

    deleteLiaAcademicTitle(guid: string): Observable<void> {
        return this.liaAcademicTitleService.deleteLiaAcademicTitle(this.caseId, guid);
    }

    createOrUpdateLiaAcademicTitles(input: CreateOrUpdateLiaAcademicTitleInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.AcademicTitle);
        return this.liaAcademicTitleService.createOrUpdateLiaAcademicTitle(input).pipe(tap((output: CreateOrUpdateLiaAcademicTitleOutput) => {
            this.lia.liaAcademicTitles.titles = output.liaAcademicTitles;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getLiaAcademicTitleFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaAcademicTitleService.getLiaAcademicTitleFiles(caseId, LiaStatus.AcademicTitle).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaAcademicTitles.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaAcademicTitles.documents);
    }

    uploadLiaAcademicTitleFiles(liaAcademicTitleId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaAcademicTitleService.uploadFiles(liaAcademicTitleId, input);
    }

    hasEmploymentB(): boolean {
        return this.lia.liaEmployments?.employments?.length > 0;
    }

    uploadLiaProfessionalResponsibilityFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaProfessionalResponsibilityService.uploadFiles(input);
    }

    getLiaProfessionalResponsibilityFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaProfessionalResponsibilityService.getLiaProfessionalResponsibilityFiles(caseId, LiaStatus.ProfessionalResponsibility).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaProfessionalResponsibilityOutput.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaProfessionalResponsibilityOutput.documents);
    }

    liaProfessionalResponsibilityProceed(): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.ProfessionalResponsibility);
        return this.liaProfessionalResponsibilityService.proceed(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    getRestrictionFiles(forceRefresh?: boolean, hasRestriction: boolean = false, hasConditionalApproval: boolean = false, hasImpairments: boolean = false, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[] | undefined> {
        if (forceRefresh) {
            return this.liaRestrictionService.getLiaRestrictionDocuments(caseId, LiaStatus.Restriction, hasRestriction, hasConditionalApproval, hasImpairments).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaRestrictions.restrictionDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaRestrictions.restrictionDocuments);
    }

    getRestrictionFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaRestrictionService.getFile(this.applicationService.caseId, LiaStatus.Restriction, fileId);
    }

    uploadRestrictionFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaRestrictionService.uploadFiles(input);
    }

    deleteRestrictionFile(fileId: number): Observable<void> {
        return this.liaRestrictionService.deleteFile(this.applicationService.caseId, LiaStatus.Restriction, fileId);
    }

    getVaccinationFiles(forceRefresh?: boolean, hasRequestedVaccinationLicence: boolean = false, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[] | undefined> {
        if (forceRefresh) {
            return this.liaVaccinationService.getLiaVaccinationDocuments(caseId, LiaStatus.Vaccination, hasRequestedVaccinationLicence).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaVaccination.vaccinationDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaVaccination.vaccinationDocuments);
    }

    getVaccinationFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaVaccinationService.getFile(this.applicationService.caseId, LiaStatus.Vaccination, fileId);
    }

    uploadVaccinationFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaVaccinationService.uploadFiles(input);
    }

    deleteVaccinationFile(fileId: number): Observable<void> {
        return this.liaVaccinationService.deleteFile(this.applicationService.caseId, LiaStatus.Vaccination, fileId);
    }

    getVaccination(): Observable<LiaVaccinationDto> {
        return of(this.lia.liaVaccination.vaccination);
    }

    createOrupdateVaccination(input: LiaVaccinationDto): Observable<LiaStatus> {
        this.applicationService.setStepToReview(LiaStatus.Vaccination);
        const dto = new CreateOrUpdateLiaVaccinationInputDto({ caseId: this.applicationService.caseId, liaVaccination: input });
        return this.liaVaccinationService.createOrUpdateLiaVaccination(dto).pipe(tap((status: LiaStatus) => {
            this.lia.liaVaccination.vaccination = input;
            this.lia.currentStep = status;
        }));
    }

    getInvoiceAddress(): Observable<LiaInvoiceAddressDto> {
        return of(this.lia.liaInvoiceAddress).pipe(delay(1));
    }

    getInvoiceAddressDetail(): Observable<LiaInvoiceAddressDetail> {
        let detail: LiaInvoiceAddressDetail;
        if (this.lia.liaInvoiceAddress.typeOfAddressId === this.codes.address.private) {
            let fullName = this.lia.liaContact.titleId !== null ? this.l(this.lia.liaContact.titleId) + ' ' + this.lia.application.name : this.lia.application.name;
            detail = new LiaInvoiceAddressDetail({
                name: fullName,
                street: this.lia.liaContact.street,
                streetNr: this.lia.liaContact.streetNr,
                postCode: this.lia.liaContact.postCode,
                city: this.lia.liaContact.city,
                email: this.lia.liaContact.email,
                countryId: this.lia.liaContact.countryId,
            });
        } else if (this.lia.liaInvoiceAddress.typeOfAddressId === this.codes.address.business) {
            let employment = this.lia.liaEmployments?.employments?.find(x => x.id === this.lia.liaInvoiceAddress.liaEmploymentId);
            if (employment) {
                detail = new LiaInvoiceAddressDetail({
                    name: employment.businessName,
                    street: employment.street,
                    streetNr: employment.streetNr,
                    postCode: employment.postCode,
                    city: employment.city,
                    email: this.lia.liaContact.email,
                    countryId: this.lia.liaContact.countryId,
                });
            } else {
                detail = null;
            }
        } else {
            detail = null;
        }
        return of(detail).pipe(delay(0));
    }

    createAddressDropdownList(): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.lia.liaContact) });

        if (!this.onlyPrivateAddressAllowed) {

            for (let i = 0; i < this.lia.liaEmployments?.employments?.length; i++) {
                // Use employmentB id as item value
                addresses.push({ value: this.lia.liaEmployments.employments[i].id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(this.lia.liaEmployments.employments[i]) });
            }

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: LiaContactDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    private getEmploymentAddressDescription(employment: LiaEmploymentDto) {
        if (employment == null) {
            return '';
        }

        let str = employment.businessName ?? '';

        if (employment.street?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.street) : str.concat(employment.street);
        }

        if (employment.streetNr?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.streetNr) : str.concat(employment.streetNr);
        }

        if (employment.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.postCode) : str.concat(employment.postCode);
        }

        if (employment.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.city) : str.concat(employment.city);
        }

        return str;
    }

    createOrUpdateLiaInvoiceAddress(input: CreateOrUpdateLiaInvoiceAddressInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.InvoiceAddress);
        return this.liaInvoiceAddressService.createOrUpdateLiaInvoiceAddress(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaInvoiceAddress = input.liaInvoiceAddress;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }


    getSelfEmployment(): Observable<EhcSelfEmploymentDto> {
        return of(this.lia.liaSelfEmployment).pipe(delay(0));
    }

    createOrUpdateLiaSelfEmployment(input: CreateOrUpdateEhcSelfEmploymentInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.SelfEmployment);
        return this.liaSelfEmploymentService.createOrUpdateLiaSelfEmployment(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaSelfEmployment = input.ehcSelfEmployment;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }


    getEhcLanguageSkill(): Observable<EhcLanguageSkillDto> {
        return of(this.lia.liaEhcLanguageSkill).pipe(delay(0));
    }

    createOrUpdateEhcLanguageSkill(input: CreateOrUpdateEhcLanguageSkillInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.EhcLanguageSkill);
        return this.ehcLanguageSkillService.createOrUpdateLiaEhcLanguageSkill(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaEhcLanguageSkill = input.ehcLanguageSkill;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getEhcLanguageSkillFiles(hasHighSchoolDiplomaOfLanguageOfActivityRegionId: string, hasPhysicianDiplomaOfLanguageOfActivityRegionId: string, caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.ehcLanguageSkillService.getLiaEhcLanguageSkillDocuments(caseId, hasHighSchoolDiplomaOfLanguageOfActivityRegionId, hasPhysicianDiplomaOfLanguageOfActivityRegionId).pipe(
            tap((output: LiaDocumentsOutput) => {
                this.lia.liaEhcLanguageSkill.languageSkillDocuments = output.liaDocuments;
            }),
            map((output: LiaDocumentsOutput) => output.liaDocuments)
        );
    }

    getEhcLanguageSkillFile(fileId: number): Observable<GeneralFileDto> {
        return this.ehcLanguageSkillService.getFile(this.caseId, LiaStatus.EhcLanguageSkill, fileId);
    }

    uploadEhcLanguageSkillFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.ehcLanguageSkillService.uploadFiles(input);
    }

    deleteEhcLanguageSkillFile(fileId: number): Observable<void> {
        return this.ehcLanguageSkillService.deleteFile(this.caseId, LiaStatus.EhcLanguageSkill, fileId);
    }

    getLiaElectronicPatientRecord(): Observable<EhcElectronicPatientRecordDto> {
        return of(this.lia.liaElectronicPatientRecord).pipe(delay(0));
    }

    createOrUpdateLiaElectronicPatientRecord(input: CreateOrUpdateEhcElectronicPatientRecordInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.ElectronicPatientRecord);
        return this.liaElectronicPatientRecordService.createOrUpdateLiaElectronicPatientRecord(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaElectronicPatientRecord = input.ehcElectronicPatientRecord;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getLiaPractice(): Observable<LiaPracticeDto> {
        return this.liaPracticeService.getLiaPractice(this.caseId);
    }

    createBlankLiaPracticeRecord(postgraduateTitleId: string = undefined): Observable<string> {
        return this.liaPracticeService.createBlankLiaPracticeRecord(this.caseId, postgraduateTitleId);
    }

    createOrUpdateLiaPractices(input: CreateOrUpdateLiaPracticesInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Practice);
        return this.liaPracticeService.createOrUpdateLiaPractices(input).pipe(tap((output: CreateOrUpdateLiaPracticesOutput) => {
            this.lia.liaPractice = output.liaPractice;
            this.lia.liaPractice.ehcPracticeConfigSnapshot = output.liaPractice.ehcPracticeConfigSnapshot;
            this.lia.currentStep = output.status;
        })).pipe(map((output: CreateOrUpdateLiaPracticesOutput) => null));
    }

    deleteLiaPracticeRecord(guid: string): Observable<void> {
        return this.liaPracticeService.deleteLiaPracticeRecord(this.caseId, guid);
    }

    hasEmploymentOrPostgraduateTitle(): Observable<boolean> {
        return this.liaPracticeService.hasEmploymentOrPostgraduateTitle(this.caseId);
    }

    getLiaPracticeRecordFiles(
        caseId = this.caseId,
        hasExemptionAccordingArt37Kvg: boolean | undefined = undefined,
    ): Observable<GeneralDocumentGroupDto[]> {
        return this.liaPracticeService
            .getLiaPracticeRecordFiles(caseId, hasExemptionAccordingArt37Kvg)
            .pipe(
                tap((output: LiaDocumentsOutput) => {
                    this.lia.liaPractice.liaPracticeRecordDocuments = output.liaDocuments;
                })
            )
            .pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
    }

    getLiaPracticeRecordFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaPracticeService.getFile(this.caseId, LiaStatus.Practice, fileId);
    }

    uploadLiaPracticeRecordFiles(ehcPracticeRecordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPracticeService.uploadFiles(ehcPracticeRecordId, input);
    }

    deleteLiaPracticeRecordFile(fileId: number): Observable<void> {
        return this.liaPracticeService.deleteFile(this.caseId, LiaStatus.Practice, fileId);
    }

    createOrUpdateLiaQualityRequirement(input: CreateOrUpdateEhcQualityRequirementInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.QualityRequirement);
        return this.liaQualityRequirementService
            .createOrUpdateLiaQualityRequirement(input)
            .pipe(
                tap((status: LiaStatus) => {
                    this.lia.liaQualityRequirement = input.ehcQualityRequirement;
                    this.lia.currentStep = status;
                })
            )
            .pipe(map((status: LiaStatus) => null));
    }

    getLiaQualityRequirement(): Observable<EhcQualityRequirementDto> {
        return of(this.lia.liaQualityRequirement).pipe(delay(0));
    }

    getLiaQualityRequirementFiles(caseId = this.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.liaQualityRequirementService.getLiaQualityRequirementDocuments(caseId, LiaStatus.QualityRequirement).pipe(
            map((output: LiaDocumentsOutput) => output.liaDocuments)
        );
    }

    uploadLiaQualityRequirementFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaQualityRequirementService.uploadFiles(input);
    }

    getLiaEmploymentOrPostgraduateTitle(): Observable<EhcEmploymentOrPostgraduateTitleDto[]> {
        return of(this.lia.liaEmploymentOrPostgraduateTitles).pipe(delay(0));
    }

    getLiaEmploymentOrPostgraduateTitleInitialList(): Observable<EhcEmploymentOrPostgraduateTitleDto[]> {
        return this.liaEmploymentOrPostgraduateTitleService.getInitialList(this.caseId);
    }

    createOrUpdateLiaEmploymentOrPostgraduateTitles(input: CreateOrUpdateEhcEmploymentOrPostgraduateTitleInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.EmploymentOrPostgraduateTitle);
        return this.liaEmploymentOrPostgraduateTitleService.createOrUpdateLiaEmploymentOrPostgraduateTitles(input).pipe(tap((output: CreateOrUpdateLiaEmploymentOrPostgraduateTitlesOutput) => {
            this.lia.liaEmploymentOrPostgraduateTitles = input.ehcEmploymentOrPostgraduateTitles;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }

    ehcEmploymentOrPostgraduateTitlesCheckLimits(): Observable<EhcEmploymentOrPostgraduateTitleLimitResultDto[]> {
        return this.liaEmploymentOrPostgraduateTitleService.checkLimits(this.caseId);
    }

    checkDocuments(): Observable<LiaStatus[]> {
        return this.liaTypeService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    static getUrlForStep(step: LiaStatus): string {
        switch (step) {
            case LiaStatus.Person:
                return 'person';
            case LiaStatus.Contact:
                return 'contact';
            case LiaStatus.Restriction:
                return 'restriction';
            case LiaStatus.LanguageSkill:
                return 'language-skills';
            case LiaStatus.Employment:
                return 'employment';
            case LiaStatus.InvoiceAddress:
                return 'invoice-address';
            case LiaStatus.Document:
                return 'document';
            case LiaStatus.AcademicTitle:
                return 'academic-title';
            case LiaStatus.ProfessionalResponsibility:
                return 'professional-responsibility';
            case LiaStatus.TherapyMethod:
                return 'therapy-method';
            case LiaStatus.Vaccination:
                return 'vaccination';
            case LiaStatus.SelfEmployment:
                return 'self-employment';
            case LiaStatus.EhcLanguageSkill:
                return 'ehc-language-skill';
            case LiaStatus.ElectronicPatientRecord:
                return 'electronic-patient-record';
            case LiaStatus.Practice:
                return 'practice';
            case LiaStatus.QualityRequirement:
                return 'quality-requirement';
            case LiaStatus.EmploymentOrPostgraduateTitle:
                return 'employment-or-postgraduate-title';
            case LiaStatus.Release:
                return 'release';
            case LiaStatus.Released:
                return null;
            case LiaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    isEligibleForEhc(): Observable<boolean> {
        let ehc90Days = this.lia.application.type === ApplicationType.Lia90Days;
        return this.liaTypeService.isEligibleForEhc(this.lia.professionId, ehc90Days);
    }

    getLiaPersonFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaPersonService.getFile(this.applicationService.caseId, LiaStatus.Person, fileId);
    }

    deleteLiaPersonFile(fileId: number): Observable<void> {
        return this.liaPersonService.deleteFile(this.applicationService.caseId, LiaStatus.Person, fileId);
    }

    getLanguageSkill(): Observable<LiaLanguageSkillDto> {
        return of(this.lia.liaLanguageSkill).pipe(delay(1));
    }

    createOrUpdateLiaLanguageSkill(input: CreateOrUpdateLiaLanguageSkillInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.LanguageSkill);
        return this.liaLanguageSkillService.createOrUpdateLiaLanguageSkill(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaLanguageSkill = input.liaLanguageSkill;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getLanguageSkillFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaLanguageSkillService.getLiaLanguageSkillDocuments(caseId, LiaStatus.LanguageSkill).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaLanguageSkill.languageSkillDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaLanguageSkill.languageSkillDocuments);
    }

    getLanguageSkillFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaLanguageSkillService.getFile(this.applicationService.caseId, LiaStatus.LanguageSkill, fileId);
    }

    uploadLanguageSkillFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaLanguageSkillService.uploadFiles(input);
    }

    deleteLanguageSkillFile(fileId: number): Observable<void> {
        return this.liaLanguageSkillService.deleteFile(this.applicationService.caseId, LiaStatus.LanguageSkill, fileId);
    }

    getEmployments(): Observable<LiaEmploymentOutput> {
        return of(this.lia.liaEmployments).pipe(delay(1));
    }

    createOrUpdateLiaEmployments(input: CreateOrUpdateLiaEmploymentsInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Employment);
        return this.liaEmploymentsService.createOrUpdateLiaEmployments(input).pipe(tap((output: CreateOrUpdateLiaEmploymentsOutput) => {
            this.lia.liaEmployments.employments = output.liaEmployments;
            this.lia.liaEmployments.mobileEmployment = output.liaMobileEmployment;
            this.lia.currentStep = output.status;
        }))
            .pipe(map((output: CreateOrUpdateLiaEmploymentsOutput) => null));
    }

    createOrUpdateLiaEmploymentsInWaitingStatus(input: CreateOrUpdateLiaEmploymentsInput): Observable<void> {
        return this.liaEmploymentsService.createOrUpdateLiaEmploymentsWhileWaitingStatus(input).pipe(tap((output: CreateOrUpdateLiaEmploymentsInWaitingStatusOutput) => {
            if (output.isNewInvoiceAddressRequired) {
                this.lia.liaEmployments.employments = output.liaEmployments;
                this.lia.liaEmployments.mobileEmployment = output.liaMobileEmployment;
                this.router.navigate([`${AppConsts.applicantBaseUrl}/lia/${this.applicationService.caseId}/wizard/invoice-address`]);
            } else {
                this.router.navigate([`${AppConsts.applicantBaseUrl}/ehc/${output.ehcCaseId}/wizard/employment`]);
            }
        })).pipe(map(() => null));
    }

    getEmploymentFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaEmploymentsService.getLiaEmploymentDocuments(caseId, LiaStatus.Employment).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaEmployments.employmentDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaEmployments.employmentDocuments);
    }

    getEmploymentFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaEmploymentsService.getFile(this.applicationService.caseId, LiaStatus.Employment, fileId);
    }

    uploadEmploymentFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaEmploymentsService.uploadFiles(input);
    }

    deleteEmploymentFile(fileId: number): Observable<void> {
        return this.liaEmploymentsService.deleteFile(this.applicationService.caseId, LiaStatus.Employment, fileId);
    }

    hasAtLeastOneEmployment(): boolean {
        if (this.lia.liaEmployments?.employments?.length > 0 || this.lia.liaEmployments?.mobileEmployment?.hasMobileEmployment) {
            return true;
        }

        return false;
    }

    getTherapyMethodFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaTherapyMethodService.getTherapyMethodDocuments(caseId, LiaStatus.TherapyMethod).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaTherapyMethod.therapyMethodDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaTherapyMethod.therapyMethodDocuments);
    }

    getTherapyMethod(): Observable<LiaTherapyMethodDto> {
        return of(this.lia.liaTherapyMethod).pipe(delay(1));
    }

    createOrUpdateLiaTherapyMethod(input: CreateOrUpdateLiaTherapyMethodInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.TherapyMethod);
        return this.liaTherapyMethodService.createOrUpdateTherapyMethod(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaTherapyMethod = input.liaTherapyMethod;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getTherapyMethodFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaTherapyMethodService.getFile(this.applicationService.caseId, LiaStatus.TherapyMethod, fileId);
    }

    uploadTherapyMethodFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaTherapyMethodService.uploadFiles(input);
    }

    deleteTherapyMethodFile(fileId: number): Observable<void> {
        return this.liaTherapyMethodService.deleteFile(this.applicationService.caseId, LiaStatus.TherapyMethod, fileId);
    }

    setLiaOnWaitingAndCreateEhc(applicantComment: string): Observable<SetLiaOnWaitingAndCreateEhcOutput> {
        return this.liaTypeService.setLiaOnWaitingAndCreateEhc(this.lia.application.caseId, applicantComment);
    }

}
